import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, LogOut, Languages, Sparkles, ShieldCheck, Command, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import GlobalSearch from "@/components/GlobalSearch";
import HealthPill from "@/components/HealthPill";
import ApprovalsBell from "@/components/layout/ApprovalsBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

export default function Topbar({ onOpenAI }) {
  const { user, org, logout, lang, setLanguage } = useAuth();
  const t = useT();
  const [openSearch, setOpenSearch] = useState(false);
  const [openPw, setOpenPw] = useState(false);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpenSearch((s) => !s);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <>
      <header className="h-14 border-b bg-card/70 backdrop-blur-xl sticky top-0 z-40 flex items-center px-4 gap-3" data-testid="topbar">
        <button
          onClick={() => setOpenSearch(true)}
          data-testid="global-search-trigger"
          className="flex-1 max-w-2xl flex items-center gap-3 px-3 h-9 border rounded-sm bg-muted/50 hover:bg-muted transition-colors text-left"
        >
          <Search className="w-4 h-4 text-muted-foreground" />
          <span className="text-[13px] text-muted-foreground truncate">{t("search_ph")}</span>
          <kbd className="ml-auto hidden sm:inline">⌘K</kbd>
        </button>

        <div className="flex-1" />

        <HealthPill />
        <ApprovalsBell />
        <ThemeToggle variant="compact" className="hidden sm:inline-flex" />

        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenAI}
          data-testid="topbar-ai-trigger"
          className="gap-2 h-9 rounded-sm hidden sm:inline-flex hover:text-accent"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm">{t("ai_assistant")}</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1.5 h-9 rounded-sm" data-testid="lang-switcher">
              <Languages className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">{lang}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm">
            <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Language</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setLanguage("en")} data-testid="lang-en">English</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLanguage("hi")} data-testid="lang-hi">हिन्दी (Hindi)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-2 pr-3 h-9 border rounded-sm hover:bg-muted transition-colors" data-testid="user-menu-trigger">
              <div className="w-6 h-6 rounded-sm bg-accent/15 text-accent flex items-center justify-center text-xs font-semibold">
                {(user?.full_name || "U")[0]?.toUpperCase()}
              </div>
              <div className="hidden md:flex flex-col leading-tight items-start">
                <span className="text-[12px] font-medium">{user?.full_name}</span>
                <span className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">{org?.name}</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm w-60">
            <DropdownMenuLabel>
              <div className="text-sm">{user?.full_name}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
              {(user?.role === "super_admin" || user?.is_super_admin) && (
                <div className="mt-1 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest bg-accent/15 text-accent">Platform admin</div>
              )}
            </DropdownMenuLabel>
            {(user?.role === "super_admin" || user?.is_super_admin) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="super-admin-link">
                  <Link to="/admin/dashboard" className="cursor-pointer">
                    <ShieldCheck className="w-4 h-4 mr-2 text-accent" /> Zenith360-ERP Admin
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setOpenPw(true)} data-testid="change-password-btn">
              <KeyRound className="w-4 h-4 mr-2" /> Change password
            </DropdownMenuItem>
            <div className="px-2 py-1.5 sm:hidden">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Theme</div>
              <ThemeToggle variant="compact" />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <GlobalSearch open={openSearch} onOpenChange={setOpenSearch} />
      <ChangePasswordDialog open={openPw} onOpenChange={setOpenPw} />
    </>
  );
}
